import { graphql } from 'gatsby';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_LANGS, MAX_OG_DESCRIPTION_LENGTH, SHOW_NEWSLETTER_BLOCK } from '../constants';
import serializers from '../serializers';
import { withI18n } from '../utils/hocs';
import { localize } from '../utils/sanity';

import BlockContent from '@sanity/block-content-to-react';

import PageSEO from '../components/PageSEO';
import Form from '../components/ui/Form';
import Hero from '../components/ui/Hero';
import ModuleLayout from '../components/ui/ModuleLayout';
import FormModule from '../components/ui/modules/FormModule';
import SanityLayout, { CommonSettings } from '../components/ui/SanityLayout';
import SocialMediaShare from '../components/ui/SocialMediaShare';
import { SanityImageType } from '../graphql-fragments/SanityImage';
import { RawPortableText } from '../types/types';
import * as styles from './InsightPage.module.scss';

export interface Insight {
  __typename: 'SanityInsight';
  _createdAt: string;
  image: SanityImageType;
  title: string;
  slug: {
    current: string;
  };
  extract: string;
  body: RawPortableText;
}

interface LocalizedQueryData {
  sanityInsight: Insight;
  sanityWebsite: CommonSettings;
}

export const query = graphql`
  query GetInsightPageById($id: String!) {
    sanityInsight(_id: { eq: $id }) {
      ...Insight
    }
    sanityWebsite {
      contacts {
        calendlyLink
      }
    }
  }
`;

interface InsightPageProps {
  location: {
    pathname: string;
  };
  data: any;
}

const InsightPage = ({ location, data }: InsightPageProps): React.ReactElement => {
  const { i18n, t } = useTranslation();
  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: LocalizedQueryData = localize(data, langs);

  const { image: insightImage, title: insightTitle, extract, body } = localizedData.sanityInsight;

  const { sanityWebsite } = localizedData;

  const heroRef = useRef<HTMLElement | null>(null);

  return (
    <SanityLayout heroRef={heroRef}>
      <PageSEO
        pageSEO={{
          title: insightTitle,
          description: extract.substring(0, MAX_OG_DESCRIPTION_LENGTH),
          image: insightImage,
        }}
      />
      <Hero
        heroRef={heroRef}
        title={insightTitle}
        text={extract}
        backgroundType="image"
        mobileBackgroundImage={insightImage}
        desktopBackgroundImage={insightImage}
        calendlyLink={sanityWebsite.contacts.calendlyLink}
        onlyText
      />
      <ModuleLayout>
        <div className={styles.contentContainer}>
          <div className={styles.insightContainer}>
            <BlockContent
              className={styles.body}
              renderContainerOnSingleChild
              blocks={body}
              serializers={serializers}
            ></BlockContent>
          </div>
          <div className={styles.shareAndNewsletterContainer}>
            <div className={styles.shareContainer}>
              <span className={styles.columnTitle}>
                {t('insight_page.share_title', 'Partilhe nas redes sociais')}
              </span>
              <SocialMediaShare />
            </div>
            {SHOW_NEWSLETTER_BLOCK && (
              <div className={styles.newsletterContainer}>
                <span className={styles.columnTitle}>
                  {t('footer.newsletter_title', 'Subscreva a nossa newsletter')}
                </span>
                <Form
                  isNewsletter
                  sendDataToMailchimp
                  formName="newsletter"
                  hideLabels
                  withSubmitButtonInsideInput
                  submitButtonText={t('footer.newsletter_button', 'Submeter')}
                  thankYouScreen={{ title: 'Thank you' }}
                  fields={[
                    {
                      crmFieldId: 'email',
                      id: 'email',
                      fieldType: 'textSingleLine',
                      title: 'Email',
                      withEmailValidation: true,
                    },
                  ]}
                />
              </div>
            )}
          </div>
        </div>
      </ModuleLayout>
      <FormModule />
    </SanityLayout>
  );
};

export default withI18n(InsightPage);
